import { formatCashClosingTime } from "./formatCashClosingTime";
import ThemedElement from "shared/components/ThemedElement";

import { REPORT_COMPANY } from "modules/came/shared/reportCompany";
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Chip,
  Box,
  Grid,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  IconButton,
  Typography,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slide,
} from "@mui/material";
import {
  Search,
  Clear,
  FilterAlt,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  LocalAtm,
  CreditCard,
  VpnKey,
  DirectionsCar,
  TwoWheeler,
  PedalBike,
  Business,
  PictureAsPdf,
  Close,
  Description,
  Info,
  Warning,
  Event,
  Person,
  CorporateFare,
} from "@mui/icons-material";

// Material Dashboard 2 React components
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

// Material Dashboard 2 React example components
import DataTable from "shared/components/Tables/DataTable";
import useFormInforme from "../../hooks/useFormInforme";
import { generarPDFInforme } from "./GeneradorPDFInforme";

// Transition para el diálogo
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Componente de Loading Spinner
const LoadingSpinner = ({ size = 20, color = "#22c55e" }) => (
  <ThemedElement
    component="div"
    style={{
      width: size,
      height: size,
      border: `2px solid ${color}30`,
      borderTop: `2px solid ${color}`,
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    }}
  />
);

LoadingSpinner.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
};

// Helper functions
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value) => {
  if (!value && value !== 0) return "0";
  return value.toLocaleString("es-CO");
};

// Función para obtener la fecha y hora actual
const getCurrentDateTime = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

// Función para limpiar el nombre del comercio (quitar el código /xxx)
const cleanComercioName = (comercio) => {
  if (!comercio) return "";
  return comercio.split("/")[0];
};

// Función para obtener la fecha máxima (hoy)
const getMaxDate = () => {
  return new Date().toISOString().split("T")[0];
};

const FormInforme = ({ onSubmit, initialData = {}, loading: externalLoading = false }) => {
  const formState = useFormInforme(onSubmit, initialData);

  const [expanded, setExpanded] = useState(true);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState(getCurrentDateTime());

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(getCurrentDateTime());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const {
    values,
    errors,
    touched,
    loading,
    submitError,
    reportData,
    users,
    loadingUsers,
    errorUsers,
    boxes,
    loadingBoxes,
    errorBoxes,
    handleChange,
    handleBlur,
    setFieldValue,
    executeSubmit,
  } = formState;

  const empresaData = { ...REPORT_COMPANY, fechaActualizacion: currentDateTime };

  const formatFecha = (fecha) => {
    if (!fecha) return "";
    const [year, month, day] = fecha.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleApplyFilters = () => {
    executeSubmit();
  };

  const handleClearAll = () => {
    const today = getMaxDate();
    setFieldValue("init", today);
    setFieldValue("end", today);
    setFieldValue("user_kme", "TODOS");
    setFieldValue("box", "TODAS");
  };

  const hasActiveFilters =
    values.init || values.end || values.user_kme !== "TODOS" || values.box !== "TODAS";

  const handleExportPDF = async () => {
    if (!reportData) {
      setPdfError("No hay datos para exportar. Realice una búsqueda primero.");
      setPdfDialogOpen(true);
      return;
    }

    setGeneratingPDF(true);
    setPdfError(null);

    try {
      const pdfData = {
        formData: {
          init: values.init,
          end: values.end,
          user_kme: values.user_kme,
          box: values.box,
          fechaFormateada: `${formatFecha(values.init)} - ${formatFecha(values.end)}`,
        },
        reportData: reportData,
        empresaData: empresaData,
      };

      const result = await generarPDFInforme(pdfData);

      if (result.success) {
        setPdfDialogOpen(false);
      } else {
        setPdfError(result.error || "Error al generar el PDF");
        setPdfDialogOpen(true);
      }
    } catch (error) {
      console.error("Error generando PDF:", error);
      setPdfError(error.message || "Error al generar el PDF");
      setPdfDialogOpen(true);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setPdfError(null);
  };

  // ========== DATOS PARA TABLAS ==========

  // Obtener la caja que se mostrará en CIERRE DE CAJA
  const getCajaDisplayValue = () => {
    if (
      values.box !== "TODAS" &&
      values.box !== undefined &&
      values.box !== null &&
      values.box !== ""
    ) {
      return String(values.box);
    }

    const boxLast = reportData?.raw?.login?.boxLast;
    if (boxLast !== undefined && boxLast !== null) {
      return `Caja ${boxLast} (Último cierre)`;
    }

    return "Todas";
  };

  // Datos para CIERRE DE CAJA (resumen en tabla)
  const cierreCajaData = reportData
    ? [
        { concepto: "USUARIO", valor: values.user_kme !== "TODOS" ? values.user_kme : "Todos" },
        { concepto: "CAJA", valor: getCajaDisplayValue() },
        {
          concepto: "RANGO DE FECHAS",
          valor: `${formatFecha(values.init)} - ${formatFecha(values.end)}`,
        },
        { concepto: "PRIMER LOGIN", valor: formatCashClosingTime(reportData.login?.primerLogin) },
        { concepto: "ÚLTIMO CIERRE", valor: formatCashClosingTime(reportData.login?.ultimoCierre) },
        { concepto: "PRIMERA FACTURA", valor: reportData.facturacion?.primeraFactura || "N/A" },
        { concepto: "ÚLTIMA FACTURA", valor: reportData.facturacion?.ultimaFactura || "N/A" },
      ]
    : [];

  const cierreCajaColumns = [
    { Header: "CONCEPTO", accessor: "concepto", width: "30%" },
    { Header: "VALOR", accessor: "valor", width: "70%" },
  ];

  const cierreCajaRows = cierreCajaData.map((item) => ({
    concepto: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.concepto}
      </MDTypography>
    ),
    valor: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: "bold" }}
      >
        {item.valor}
      </MDTypography>
    ),
  }));

  // Datos para MOVIMIENTO DIARIO POR TARIFA
  const movimientoData = reportData
    ? [
        { concepto: "EFECTIVO INICIAL", valor: formatCurrency(0) },
        { concepto: "RECAUDO TARIFAS", valor: formatCurrency(reportData.recaudos?.tarifas || 0) },
        { concepto: "REC. MENSUALES", valor: formatCurrency(reportData.recaudos?.mensuales || 0) },
        {
          concepto: "REC. MISCELANEOS",
          valor: formatCurrency(reportData.recaudos?.miselaneos || 0),
        },
        { concepto: "RETIROS", valor: formatCurrency(reportData.recaudos?.retiros || 0) },
        { concepto: "REC. PERDIDOS", valor: formatCurrency(reportData.recaudos?.perdidos || 0) },
        {
          concepto: "BALANCE DE CAJA",
          valor: formatCurrency(reportData.recaudos?.balanceCaja || 0),
        },
      ]
    : [];

  const movimientoColumns = [
    { Header: "CONCEPTO", accessor: "concepto", width: "50%" },
    { Header: "VALOR", accessor: "valor", width: "50%" },
  ];

  const movimientoRows = movimientoData.map((item) => ({
    concepto: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.concepto}
      </MDTypography>
    ),
    valor: (
      <MDTypography
        variant="body2"
        sx={{
          fontSize: "0.875rem",
          color: item.concepto === "BALANCE DE CAJA" ? "#ed6c02" : "#1a202c",
          fontWeight: "bold",
        }}
      >
        {item.valor}
      </MDTypography>
    ),
  }));

  // Datos para RECAUDOS
  const recaudosData = reportData
    ? [
        {
          descripcion: "TARIFAS",
          cantidad: reportData.recaudosTable?.tarifas?.cantidad || 0,
          total: reportData.recaudosTable?.tarifas?.total || 0,
        },
        {
          descripcion: "PERDIDOS",
          cantidad: reportData.recaudosTable?.perdidos?.cantidad || 0,
          total: reportData.recaudosTable?.perdidos?.total || 0,
        },
        { descripcion: "CONTINGENCIAS", cantidad: 0, total: 0 },
        {
          descripcion: "MENSUALES",
          cantidad: reportData.recaudosTable?.mensuales?.cantidad || 0,
          total: reportData.recaudosTable?.mensuales?.total || 0,
        },
        { descripcion: "RECARGAS PREPAGOS", cantidad: 0, total: 0 },
        {
          descripcion: "MISCELANEOS",
          cantidad: reportData.recaudosTable?.miscelaneos?.cantidad || 0,
          total: reportData.recaudosTable?.miscelaneos?.total || 0,
        },
        {
          descripcion: "IMPUESTOS",
          cantidad: reportData.recaudosTable?.impuestos?.cantidad || 0,
          total: reportData.recaudosTable?.impuestos?.total || 0,
        },
      ]
    : [];

  const totalCantidadRecaudo = recaudosData
    .filter((item) => item.descripcion !== "IMPUESTOS")
    .reduce((sum, item) => sum + (item.cantidad || 0), 0);

  // ✅ Total recaudo usando el BALANCE DE CAJA
  const totalRecaudo = reportData?.recaudos?.balanceCaja || 0;

  const recaudosColumns = [
    { Header: "DESCRIPCION", accessor: "descripcion", width: "50%" },
    { Header: "CANTIDAD", accessor: "cantidad", width: "25%" },
    { Header: "TOTAL", accessor: "total", width: "25%" },
  ];

  const recaudosRows = recaudosData.map((item) => ({
    descripcion: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.descripcion}
      </MDTypography>
    ),
    cantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(item.cantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(item.total)}
      </MDTypography>
    ),
  }));

  recaudosRows.push({
    descripcion: (
      <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
        TOTAL RECAUDO
      </MDTypography>
    ),
    cantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(totalCantidadRecaudo)}
      </MDTypography>
    ),
    total: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#2e7d32" }}>
        {formatCurrency(totalRecaudo)}
      </MDTypography>
    ),
  });

  // Datos para CONTADORES DE CAJERO
  const contadoresData = [
    { concepto: "TRANSACCIONES", valor: formatNumber(reportData?.contadores?.transacciones || 0) },
    { concepto: "CANCELACIONES", valor: formatNumber(reportData?.contadores?.cancelaciones || 0) },
    {
      concepto: "INICIO DE SISTEMA",
      valor: formatNumber(reportData?.contadores?.inicioSistema || 0),
    },
    {
      concepto: "APERTURA BARRERA ENTRADA",
      valor: formatNumber(reportData?.contadores?.aperturaBarreraEntrada || 0),
    },
    {
      concepto: "APERTURA BARRERA SALIDA",
      valor: formatNumber(reportData?.contadores?.aperturaBarreraSalida || 0),
    },
    {
      concepto: "SALIDA EN CONTINGENCIA",
      valor: formatNumber(reportData?.contadores?.salidaContingencia || 0),
    },
    {
      concepto: "TIQUETES PURGADOS",
      valor: formatNumber(reportData?.contadores?.tiquetesPurgados || 0),
    },
  ];

  const contadoresColumns = [
    { Header: "CONCEPTO", accessor: "concepto", width: "50%" },
    { Header: "VALOR", accessor: "valor", width: "50%" },
  ];

  const contadoresRows = contadoresData.map((item) => ({
    concepto: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.concepto}
      </MDTypography>
    ),
    valor: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: "bold" }}
      >
        {item.valor}
      </MDTypography>
    ),
  }));

  // ============================================
  // CONTEO VEHICULAR
  // ============================================

  const facturasRaw = reportData?.raw?.facturasEmiAnu || [];
  const facturaAnulada = facturasRaw.find((item) => item.status_descripcion === "ANULADA");
  const facturasAnuladas = facturaAnulada ? parseInt(facturaAnulada.total, 10) : 0;
  const vehiculosConTiquetes = reportData?.conteoResumen?.vehiculosConTiquetes || 0;
  const tiquetesPerdidos = reportData?.conteoResumen?.tiquetesPerdidos || 0;
  const salidaEnGracia = 0;
  const facturasEmitidasCalculadas = vehiculosConTiquetes - facturasAnuladas - salidaEnGracia;
  const sinFacturaPorDescuento = reportData?.conteoResumen?.sinFacturaPorDescuento || 0;

  const conteoResumenData = [
    { concepto: "VEHÍCULOS CON TIQUETES", valor: formatNumber(vehiculosConTiquetes) },
    { concepto: "TIQUETES PERDIDOS", valor: formatNumber(tiquetesPerdidos) },
    { concepto: "SALIDA EN GRACIA", valor: formatNumber(salidaEnGracia) },
    { concepto: "FACTURAS EMITIDAS", valor: formatNumber(facturasEmitidasCalculadas) },
    { concepto: "FACTURAS ANULADAS", valor: formatNumber(facturasAnuladas) },
    { concepto: "SIN FACTURA POR DESCUENTO", valor: formatNumber(sinFacturaPorDescuento) },
  ];

  const conteoResumenColumns = [
    { Header: "CONCEPTO", accessor: "concepto", width: "50%" },
    { Header: "VALOR", accessor: "valor", width: "50%" },
  ];

  const conteoResumenRows = conteoResumenData.map((item) => ({
    concepto: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.concepto}
      </MDTypography>
    ),
    valor: (
      <MDTypography
        variant="body2"
        sx={{
          fontSize: "0.875rem",
          color:
            item.concepto === "TIQUETES PERDIDOS"
              ? "#d32f2f"
              : item.concepto === "FACTURAS EMITIDAS"
              ? "#2e7d32"
              : "#1a202c",
          fontWeight: "bold",
        }}
      >
        {item.valor}
      </MDTypography>
    ),
  }));

  const conteoVehicularTableData = reportData?.conteoVehicularTable || [
    { codigo: "T1", descripcion: "CARRO", cantidad: 0, total: 0 },
    { codigo: "T1", descripcion: "CARRO SIN COBRO", cantidad: 0, total: 0 },
    { codigo: "T2", descripcion: "MOTOS", cantidad: 0, total: 0 },
    { codigo: "T2", descripcion: "MOTOS SIN COBRO", cantidad: 0, total: 0 },
    { codigo: "T3", descripcion: "BICICLETAS", cantidad: 0, total: 0 },
    { codigo: "T3", descripcion: "BICI SIN COBROS", cantidad: 0, total: 0 },
  ];

  const conteoVehicularColumns = [
    { Header: "CODIGO", accessor: "codigo", width: "15%" },
    { Header: "DESCRIPCION", accessor: "descripcion", width: "45%" },
    { Header: "CANTIDAD", accessor: "cantidad", width: "20%" },
    { Header: "TOTAL", accessor: "total", width: "20%" },
  ];

  const conteoVehicularRows = conteoVehicularTableData.map((item) => ({
    codigo: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.codigo}
      </MDTypography>
    ),
    descripcion: (
      <MDBox display="flex" alignItems="center" gap={1}>
        {item.descripcion.includes("CARRO") && (
          <DirectionsCar fontSize="small" sx={{ color: "#4a5568" }} />
        )}
        {item.descripcion.includes("MOTO") && (
          <TwoWheeler fontSize="small" sx={{ color: "#4a5568" }} />
        )}
        {item.descripcion.includes("BICI") && (
          <PedalBike fontSize="small" sx={{ color: "#4a5568" }} />
        )}
        <MDTypography
          variant="body2"
          sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
        >
          {item.descripcion}
        </MDTypography>
      </MDBox>
    ),
    cantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(item.cantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(item.total)}
      </MDTypography>
    ),
  }));

  // Datos para RECAUDO POR PERDIDOS
  const perdidosCantidad = reportData?.movimientos?.countTicketPerdidos || 0;
  const perdidosTotal = reportData?.movimientos?.ticketesPerdidos || 0;

  const recaudoPerdidosData = [
    { codigo: "VEHI", descripcion: "Vehículos", cantidad: perdidosCantidad, total: perdidosTotal },
  ];

  const recaudoPerdidosColumns = [
    { Header: "CODIGO", accessor: "codigo", width: "20%" },
    { Header: "DESCRIPCION", accessor: "descripcion", width: "40%" },
    { Header: "CANTIDAD", accessor: "cantidad", width: "20%" },
    { Header: "TOTAL", accessor: "total", width: "20%" },
  ];

  const recaudoPerdidosRows = recaudoPerdidosData.map((item) => ({
    codigo: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.codigo}
      </MDTypography>
    ),
    descripcion: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.descripcion}
      </MDTypography>
    ),
    cantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(item.cantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(item.total)}
      </MDTypography>
    ),
  }));

  recaudoPerdidosRows.push({
    codigo: (
      <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
        TOTAL PERDIDOS
      </MDTypography>
    ),
    descripcion: "",
    cantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(perdidosCantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#2e7d32" }}>
        {formatCurrency(perdidosTotal)}
      </MDTypography>
    ),
  });

  // Datos para RECAUDO POR MISCELANEOS
  const miscelaneosCantidad = reportData?.movimientos?.miselaneos || 0;
  const miscelaneosTotal = reportData?.movimientos?.totalMiselaneos || 0;

  const recaudoMiscelaneosData = [
    {
      codigo: "01",
      descripcion: "Misceláneos",
      cantidad: miscelaneosCantidad,
      total: miscelaneosTotal,
    },
  ];

  const recaudoMiscelaneosRows = recaudoMiscelaneosData.map((item) => ({
    codigo: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.codigo}
      </MDTypography>
    ),
    descripcion: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.descripcion}
      </MDTypography>
    ),
    cantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(item.cantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(item.total)}
      </MDTypography>
    ),
  }));

  recaudoMiscelaneosRows.push({
    codigo: (
      <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
        TOTAL MISCELANEOS
      </MDTypography>
    ),
    descripcion: "",
    cantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(miscelaneosCantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#2e7d32" }}>
        {formatCurrency(miscelaneosTotal)}
      </MDTypography>
    ),
  });

  // Datos para RECAUDO POR RECARGAS DE PREPAGO
  const recaudoPrepagoData = [
    { codigo: "02", descripcion: "REC. PREPAGOS", cantidad: 0, total: 0 },
  ];

  const recaudoPrepagoRows = recaudoPrepagoData.map((item) => ({
    codigo: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.codigo}
      </MDTypography>
    ),
    descripcion: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.descripcion}
      </MDTypography>
    ),
    cantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(item.cantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(item.total)}
      </MDTypography>
    ),
  }));

  recaudoPrepagoRows.push({
    codigo: (
      <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
        TOTAL RECARGAS PREPAGO
      </MDTypography>
    ),
    descripcion: "",
    cantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(0)}
      </MDTypography>
    ),
    total: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#2e7d32" }}>
        {formatCurrency(0)}
      </MDTypography>
    ),
  });

  // Datos para RECAUDO POR IMPUESTOS
  const impuestosCantidad = reportData?.recaudosTable?.impuestos?.cantidad || 0;
  const impuestosTotal = reportData?.recaudosTable?.impuestos?.total || 0;

  const recaudoImpuestosData = [
    { codigo: "103", descripcion: "IVA 19%", cantidad: impuestosCantidad, total: impuestosTotal },
  ];

  const recaudoImpuestosRows = recaudoImpuestosData.map((item) => ({
    codigo: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.codigo}
      </MDTypography>
    ),
    descripcion: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.descripcion}
      </MDTypography>
    ),
    cantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(item.cantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(item.total)}
      </MDTypography>
    ),
  }));

  recaudoImpuestosRows.push({
    codigo: (
      <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
        TOTAL IMPUESTOS
      </MDTypography>
    ),
    descripcion: "",
    cantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(impuestosCantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#2e7d32" }}>
        {formatCurrency(impuestosTotal)}
      </MDTypography>
    ),
  });

  // ============================================
  // DESCUENTO DE COMERCIOS
  // ============================================

  const procesarDescuentosComercio = () => {
    const descuentos = reportData?.movimientos?.descuentosComercio || [];

    if (descuentos.length === 0) {
      return [];
    }

    return descuentos.map((comercio, index) => {
      const nombreComercio = cleanComercioName(comercio.comercio);
      const totalCantidad =
        (comercio.countDescuentoCarroCommercio || 0) +
        (comercio.countDescuentoMotoCommercio || 0) +
        (comercio.countDescuentoBiciCommercio || 0);
      const totalValor =
        (comercio.descuentoCarroCommercio || 0) +
        (comercio.descuentoMotoCommercio || 0) +
        (comercio.descuentoBiciCommercio || 0);

      return {
        codigo: index + 1,
        descripcion: nombreComercio,
        carroCantidad: comercio.countDescuentoCarroCommercio || 0,
        carroTotal: comercio.descuentoCarroCommercio || 0,
        motoCantidad: comercio.countDescuentoMotoCommercio || 0,
        motoTotal: comercio.descuentoMotoCommercio || 0,
        biciCantidad: comercio.countDescuentoBiciCommercio || 0,
        biciTotal: comercio.descuentoBiciCommercio || 0,
        totalCantidad: totalCantidad,
        totalValor: totalValor,
      };
    });
  };

  const calcularTotalesDescuentos = (data) => {
    if (data.length === 0) {
      return {
        totalCarroCantidad: 0,
        totalCarroValor: 0,
        totalMotoCantidad: 0,
        totalMotoValor: 0,
        totalBiciCantidad: 0,
        totalBiciValor: 0,
        totalGeneralCantidad: 0,
        totalGeneralValor: 0,
      };
    }

    return data.reduce(
      (acc, item) => ({
        totalCarroCantidad: acc.totalCarroCantidad + (item.carroCantidad || 0),
        totalCarroValor: acc.totalCarroValor + (item.carroTotal || 0),
        totalMotoCantidad: acc.totalMotoCantidad + (item.motoCantidad || 0),
        totalMotoValor: acc.totalMotoValor + (item.motoTotal || 0),
        totalBiciCantidad: acc.totalBiciCantidad + (item.biciCantidad || 0),
        totalBiciValor: acc.totalBiciValor + (item.biciTotal || 0),
        totalGeneralCantidad: acc.totalGeneralCantidad + (item.totalCantidad || 0),
        totalGeneralValor: acc.totalGeneralValor + (item.totalValor || 0),
      }),
      {
        totalCarroCantidad: 0,
        totalCarroValor: 0,
        totalMotoCantidad: 0,
        totalMotoValor: 0,
        totalBiciCantidad: 0,
        totalBiciValor: 0,
        totalGeneralCantidad: 0,
        totalGeneralValor: 0,
      }
    );
  };

  const descuentosComercioData = procesarDescuentosComercio();
  const totalesDescuentos = calcularTotalesDescuentos(descuentosComercioData);

  const comerciosColumns = [
    { Header: "CODIGO", accessor: "codigo", width: "5%" },
    { Header: "DESCRIPCION", accessor: "descripcion", width: "12%" },
    { Header: "CARRO (Cant)", accessor: "carroCantidad", width: "8%" },
    { Header: "CARRO (Total)", accessor: "carroTotal", width: "12%" },
    { Header: "MOTO (Cant)", accessor: "motoCantidad", width: "8%" },
    { Header: "MOTO (Total)", accessor: "motoTotal", width: "12%" },
    { Header: "BICI (Cant)", accessor: "biciCantidad", width: "8%" },
    { Header: "BICI (Total)", accessor: "biciTotal", width: "12%" },
    { Header: "TOTAL (Cant)", accessor: "totalCantidad", width: "8%" },
    { Header: "TOTAL (Total)", accessor: "totalValor", width: "15%" },
  ];

  const comerciosRows = descuentosComercioData.map((comercio) => ({
    codigo: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {comercio.codigo}
      </MDTypography>
    ),
    descripcion: (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Business fontSize="small" sx={{ color: "#4a5568" }} />
        <MDTypography
          variant="body2"
          sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
        >
          {comercio.descripcion}
        </MDTypography>
      </MDBox>
    ),
    carroCantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(comercio.carroCantidad)}
      </MDTypography>
    ),
    carroTotal: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(comercio.carroTotal)}
      </MDTypography>
    ),
    motoCantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(comercio.motoCantidad)}
      </MDTypography>
    ),
    motoTotal: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(comercio.motoTotal)}
      </MDTypography>
    ),
    biciCantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(comercio.biciCantidad)}
      </MDTypography>
    ),
    biciTotal: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(comercio.biciTotal)}
      </MDTypography>
    ),
    totalCantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: "bold" }}
      >
        {formatNumber(comercio.totalCantidad)}
      </MDTypography>
    ),
    totalValor: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: "bold" }}
      >
        {formatCurrency(comercio.totalValor)}
      </MDTypography>
    ),
  }));

  comerciosRows.push({
    codigo: (
      <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
        TOTALES
      </MDTypography>
    ),
    descripcion: "",
    carroCantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(totalesDescuentos.totalCarroCantidad)}
      </MDTypography>
    ),
    carroTotal: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatCurrency(totalesDescuentos.totalCarroValor)}
      </MDTypography>
    ),
    motoCantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(totalesDescuentos.totalMotoCantidad)}
      </MDTypography>
    ),
    motoTotal: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatCurrency(totalesDescuentos.totalMotoValor)}
      </MDTypography>
    ),
    biciCantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(totalesDescuentos.totalBiciCantidad)}
      </MDTypography>
    ),
    biciTotal: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatCurrency(totalesDescuentos.totalBiciValor)}
      </MDTypography>
    ),
    totalCantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(totalesDescuentos.totalGeneralCantidad)}
      </MDTypography>
    ),
    totalValor: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#2e7d32" }}>
        {formatCurrency(totalesDescuentos.totalGeneralValor)}
      </MDTypography>
    ),
  });

  // Datos para PAGOS CON PREPAGO
  const pagosPrepagoDataTable = [{ descripcion: "Vehículos", cantidad: 0, total: 0 }];

  const pagosPrepagoColumns = [
    { Header: "DESCRIPCION", accessor: "descripcion", width: "60%" },
    { Header: "CANTIDAD", accessor: "cantidad", width: "20%" },
    { Header: "TOTAL", accessor: "total", width: "20%" },
  ];

  const pagosPrepagoRows = pagosPrepagoDataTable.map((item) => ({
    descripcion: (
      <MDTypography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {item.descripcion}
      </MDTypography>
    ),
    cantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(item.cantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(item.total)}
      </MDTypography>
    ),
  }));

  pagosPrepagoRows.push({
    descripcion: (
      <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
        TOTAL PAGOS
      </MDTypography>
    ),
    cantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(0)}
      </MDTypography>
    ),
    total: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#2e7d32" }}>
        {formatCurrency(0)}
      </MDTypography>
    ),
  });

  // ============================================
  // FORMAS DE PAGO
  // ============================================

  const formasPagoEfectivoTotal = reportData?.totals?.formasPago?.efectivo?.total || 0;
  const formasPagoEfectivoCount = reportData?.totals?.formasPago?.efectivo?.count || 0;
  const formasPagoTarjetaTotal = reportData?.totals?.formasPago?.tarjeta?.total || 0;
  const formasPagoTarjetaCount = reportData?.totals?.formasPago?.tarjeta?.count || 0;

  const totalFormasPagoCantidad = formasPagoEfectivoCount + formasPagoTarjetaCount;
  const totalFormasPagoValor = formasPagoEfectivoTotal + formasPagoTarjetaTotal;

  const formasPagoDataTable = [
    {
      descripcion: "EFECTIVO",
      cantidad: formasPagoEfectivoCount,
      total: formasPagoEfectivoTotal,
    },
    {
      descripcion: "TARJETA",
      cantidad: formasPagoTarjetaCount,
      total: formasPagoTarjetaTotal,
    },
    { descripcion: "PREPAGO", cantidad: 0, total: 0 },
  ];

  const formasPagoColumns = [
    { Header: "DESCRIPCION", accessor: "descripcion", width: "50%" },
    { Header: "CANTIDAD", accessor: "cantidad", width: "25%" },
    { Header: "TOTAL", accessor: "total", width: "25%" },
  ];

  const formasPagoRows = formasPagoDataTable.map((forma) => ({
    descripcion: (
      <MDBox display="flex" alignItems="center" gap={1}>
        {forma.descripcion === "EFECTIVO" && (
          <LocalAtm fontSize="small" sx={{ color: "#1976d2" }} />
        )}
        {forma.descripcion === "TARJETA" && (
          <CreditCard fontSize="small" sx={{ color: "#0288d1" }} />
        )}
        {forma.descripcion === "PREPAGO" && <VpnKey fontSize="small" sx={{ color: "#9c27b0" }} />}
        <MDTypography
          variant="body2"
          sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
        >
          {forma.descripcion}
        </MDTypography>
      </MDBox>
    ),
    cantidad: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatNumber(forma.cantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography
        variant="body2"
        align="right"
        sx={{ fontSize: "0.875rem", color: "#1a202c", fontWeight: 500 }}
      >
        {formatCurrency(forma.total)}
      </MDTypography>
    ),
  }));

  formasPagoRows.push({
    descripcion: (
      <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
        TOTAL RECAUDADO
      </MDTypography>
    ),
    cantidad: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#1a202c" }}>
        {formatNumber(totalFormasPagoCantidad)}
      </MDTypography>
    ),
    total: (
      <MDTypography variant="body2" fontWeight="bold" align="right" sx={{ color: "#2e7d32" }}>
        {formatCurrency(totalFormasPagoValor)}
      </MDTypography>
    ),
  });

  return (
    <MDBox>
      {/* Diálogo de confirmación para PDF */}
      <Dialog
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
          },
        }}
      >
        {generatingPDF ? (
          <>
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                p: 3,
                textAlign: "center",
              }}
            >
              <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
              <Typography variant="h6" sx={{ color: "white", fontWeight: "bold" }}>
                Generando PDF
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 1 }}>
                Por favor espere, esto puede tomar unos segundos...
              </Typography>
            </Box>
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: "#22c55e",
                    borderRadius: "50%",
                    animation: "pulse 1.5s infinite",
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: "#22c55e",
                    borderRadius: "50%",
                    animation: "pulse 1.5s infinite 0.5s",
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: "#22c55e",
                    borderRadius: "50%",
                    animation: "pulse 1.5s infinite 1s",
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: "#718096" }}>
                Preparando el documento...
              </Typography>
            </Box>
          </>
        ) : pdfError ? (
          <>
            <Box
              sx={{
                background: "linear-gradient(135deg, #f56565 0%, #ed8936 100%)",
                p: 3,
                textAlign: "center",
              }}
            >
              <Warning sx={{ fontSize: 60, color: "white", mb: 1 }} />
              <Typography variant="h6" sx={{ color: "white", fontWeight: "bold" }}>
                Error al generar PDF
              </Typography>
            </Box>
            <DialogContent sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body1" sx={{ color: "#e53e3e", mb: 2 }}>
                {pdfError}
              </Typography>
              <Typography variant="body2" sx={{ color: "#718096" }}>
                Verifique su conexión y vuelva a intentarlo.
                <br />
                Si el problema persiste, contacte al administrador.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0, justifyContent: "center" }}>
              <Button
                onClick={handleClosePdfDialog}
                variant="contained"
                sx={{
                  backgroundColor: "#e53e3e",
                  color: "white",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 4,
                  "&:hover": { backgroundColor: "#c53030" },
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <Box
              sx={{
                background: "linear-gradient(135deg, #2e8238ff 0%, #2e8840ff 100%)",
                p: 3,
                textAlign: "center",
              }}
            >
              <Description sx={{ fontSize: 50, color: "white", mb: 1 }} />
              <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.8)", fontWeight: "bold" }}>
                Exportar a PDF
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.5 }}>
                Confirme los detalles del informe
              </Typography>
            </Box>

            <DialogContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#1a202c", mb: 2 }}>
                Resumen del informe:
              </Typography>

              <List sx={{ bgcolor: "#f7fafc", borderRadius: "12px", p: 0, overflow: "hidden" }}>
                <ListItem sx={{ borderBottom: "1px solid #e2e8f0", bgcolor: "white" }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Event sx={{ color: "#22c55e" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Rango de fechas"
                    secondary={`${formatFecha(values.init)} - ${formatFecha(values.end)}`}
                    primaryTypographyProps={{ variant: "body2", fontWeight: "bold" }}
                    secondaryTypographyProps={{ variant: "body2", color: "#4a5568" }}
                  />
                </ListItem>
                <ListItem sx={{ borderBottom: "1px solid #e2e8f0", bgcolor: "white" }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Person sx={{ color: "#3182ce" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Usuario"
                    secondary={values.user_kme !== "TODOS" ? values.user_kme : "Todos"}
                    primaryTypographyProps={{ variant: "body2", fontWeight: "bold" }}
                    secondaryTypographyProps={{ variant: "body2", color: "#4a5568" }}
                  />
                </ListItem>
                <ListItem sx={{ borderBottom: "1px solid #e2e8f0", bgcolor: "white" }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CorporateFare sx={{ color: "#805ad5" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Caja"
                    secondary={values.box !== "TODAS" ? values.box : "Todas"}
                    primaryTypographyProps={{ variant: "body2", fontWeight: "bold" }}
                    secondaryTypographyProps={{ variant: "body2", color: "#4a5568" }}
                  />
                </ListItem>
              </List>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "#ebf8ff",
                  borderRadius: "12px",
                  border: "1px solid #bee3f8",
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Info sx={{ fontSize: 16, color: "#3182ce" }} />
                  <Typography variant="caption" sx={{ fontWeight: "bold", color: "#2c5282" }}>
                    Información adicional
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "#2b6cb0" }}>
                  • El PDF incluirá todas las secciones del informe
                  <br />
                  • Formato profesional optimizado para impresión
                  <br />• Los totales se calcularán automáticamente
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, justifyContent: "center", gap: 2 }}>
              <Button
                onClick={handleClosePdfDialog}
                variant="outlined"
                startIcon={<Close />}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 3,
                  borderColor: "#cbd5e0",
                  color: "#4a5568",
                  "&:hover": { borderColor: "#a0aec0", backgroundColor: "#f7fafc" },
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleExportPDF}
                variant="contained"
                startIcon={<PictureAsPdf />}
                sx={{
                  backgroundColor: "#dc004e",
                  color: "#ffffffff",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 4,
                  "&:hover": { backgroundColor: "#b0003a" },
                }}
              >
                Exportar PDF
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ENCABEZADO SUPERIOR - LOGO Y EMPRESA */}
      <Paper
        elevation={0}
        sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
      >
        <MDBox sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#1a237e" }}>
                {empresaData.nombre}
              </MDTypography>
              <MDTypography variant="h6" fontWeight="regular" sx={{ color: "#2d3748", mt: 0.5 }}>
                {empresaData.servicio}
              </MDTypography>
              <MDTypography variant="subtitle2" sx={{ color: "#4a5568", mt: 0.5 }}>
                {empresaData.version}
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <MDBox sx={{ textAlign: "right" }}>
                <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  {empresaData.fechaActualizacion}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#718096" }} display="block">
                  Fecha Actualización
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <MDTypography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: "#1a202c" }}>
                LICENCIADO A:
              </MDTypography>
              <MDBox sx={{ pl: 2 }}>
                <MDTypography variant="body2" sx={{ mb: 0.5, color: "#2d3748" }}>
                  <strong>RAZON SOCIAL:</strong> {empresaData.razonSocial}
                </MDTypography>
                <MDTypography variant="body2" sx={{ mb: 0.5, color: "#2d3748" }}>
                  <strong>NIT:</strong> {empresaData.nit}
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: "#2d3748" }}>
                  <strong>#ID MAQUINA:</strong> {empresaData.idMaquina}
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Paper>

      {/* COMPONENTE DE FILTROS */}
      <MDBox mb={4}>
        <Paper
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            border: "1px solid #e5e7eb",
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: "1px solid #f0f0f0",
              cursor: "pointer",
              backgroundColor: "#fafafa",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1.5}>
                <FilterAlt sx={{ color: "#22c55e", fontSize: 22 }} />
                <Box>
                  <MDTypography variant="h6" fontWeight="bold" color="textPrimary">
                    Filtros de Búsqueda
                  </MDTypography>
                  <MDTypography variant="caption" color="textSecondary">
                    Filtra por rango de fechas, usuario y caja
                  </MDTypography>
                </Box>
              </Box>
              <IconButton size="small" sx={{ color: "#9ca3af" }}>
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>
          {expanded && (
            <Box p={3}>
              {hasActiveFilters && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #dcfce7",
                    mb: 3,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <CheckCircle sx={{ fontSize: 16, color: "#22c55e" }} />
                    <MDTypography variant="body2" fontWeight="medium" color="success.main">
                      Filtros activos:
                    </MDTypography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {values.init && (
                      <Chip
                        size="small"
                        label={`Inicio: ${formatFecha(values.init)}`}
                        onDelete={() => setFieldValue("init", getMaxDate())}
                        sx={{
                          height: "28px",
                          fontSize: "0.75rem",
                          backgroundColor: "white",
                          border: "1px solid #bbf7d0",
                          color: "#1a202c",
                        }}
                      />
                    )}
                    {values.end && values.end !== values.init && (
                      <Chip
                        size="small"
                        label={`Fin: ${formatFecha(values.end)}`}
                        onDelete={() => setFieldValue("end", getMaxDate())}
                        sx={{
                          height: "28px",
                          fontSize: "0.75rem",
                          backgroundColor: "white",
                          border: "1px solid #bbf7d0",
                          color: "#1a202c",
                        }}
                      />
                    )}
                    {values.user_kme !== "TODOS" && (
                      <Chip
                        size="small"
                        label={`Usuario: ${values.user_kme}`}
                        onDelete={() => setFieldValue("user_kme", "TODOS")}
                        sx={{
                          height: "28px",
                          fontSize: "0.75rem",
                          backgroundColor: "white",
                          border: "1px solid #bbf7d0",
                          color: "#1a202c",
                        }}
                      />
                    )}
                    {values.box !== "TODAS" && (
                      <Chip
                        size="small"
                        label={`Caja: ${values.box}`}
                        onDelete={() => setFieldValue("box", "TODAS")}
                        sx={{
                          height: "28px",
                          fontSize: "0.75rem",
                          backgroundColor: "white",
                          border: "1px solid #bbf7d0",
                          color: "#1a202c",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )}
              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel
                      shrink
                      htmlFor="init-date-field"
                      sx={{ backgroundColor: "white", px: 1, color: "#1a202c" }}
                    >
                      Fecha Inicial
                    </InputLabel>
                    <TextField
                      id="init-date-field"
                      type="date"
                      name="init"
                      value={values.init}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.init && !!errors.init}
                      helperText={touched.init && errors.init}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        max: values.end || getMaxDate(),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#fff",
                          height: "56px",
                          "& fieldset": { borderColor: "#e5e7eb" },
                          "&:hover fieldset": { borderColor: "#22c55e" },
                          "&.Mui-focused fieldset": { borderColor: "#22c55e" },
                        },
                        "& .MuiInputBase-input": {
                          color: "#1a202c",
                          fontSize: "0.875rem",
                          padding: "16.5px 14px",
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel
                      shrink
                      htmlFor="end-date-field"
                      sx={{ backgroundColor: "white", px: 1, color: "#1a202c" }}
                    >
                      Fecha Final
                    </InputLabel>
                    <TextField
                      id="end-date-field"
                      type="date"
                      name="end"
                      value={values.end}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.end && !!errors.end}
                      helperText={touched.end && errors.end}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: values.init,
                        max: getMaxDate(),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#fff",
                          height: "56px",
                          "& fieldset": { borderColor: "#e5e7eb" },
                          "&:hover fieldset": { borderColor: "#22c55e" },
                          "&.Mui-focused fieldset": { borderColor: "#22c55e" },
                        },
                        "& .MuiInputBase-input": {
                          color: "#1a202c",
                          fontSize: "0.875rem",
                          padding: "16.5px 14px",
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth error={!!errorUsers}>
                    <InputLabel sx={{ fontSize: "0.875rem", color: "#1a202c" }}>Usuario</InputLabel>
                    <Select
                      name="user_kme"
                      value={values.user_kme}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Usuario"
                      sx={{ height: 56, color: "#1a202c" }}
                      disabled={loadingUsers}
                    >
                      {users.map((option) => (
                        <MenuItem key={option.id} value={option.value} sx={{ color: "#1a202c" }}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errorUsers && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                        {errorUsers}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth error={!!errorBoxes}>
                    <InputLabel sx={{ fontSize: "0.875rem", color: "#1a202c" }}>Caja</InputLabel>
                    <Select
                      name="box"
                      value={values.box}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Caja"
                      sx={{ height: 56, color: "#1a202c" }}
                      disabled={loadingBoxes}
                    >
                      {boxes.map((option) => (
                        <MenuItem key={option.id} value={option.value} sx={{ color: "#1a202c" }}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errorBoxes && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                        {errorBoxes}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                sx={{ mt: 2, pt: 2, borderTop: "1px solid #f0f0f0" }}
              >
                <Typography variant="caption" sx={{ color: "#4a5568" }}>
                  {values.init && values.end ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CheckCircle sx={{ fontSize: 12, color: "#22c55e" }} />
                      Listo para buscar
                    </Box>
                  ) : (
                    "Seleccione un rango de fechas"
                  )}
                </Typography>
                <Box display="flex" gap={1.5}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    disabled={loading || !values.init || !values.end}
                    startIcon={loading ? <LoadingSpinner size={18} /> : <Search />}
                    sx={{
                      backgroundColor: "#22c55e",
                      color: "#ffffffff",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      px: 3,
                      py: 0.75,
                      borderRadius: "8px",
                      boxShadow: "none",
                      "&:hover": { backgroundColor: "#16a34a" },
                      "&.Mui-disabled": { backgroundColor: alpha("#22c55e", 0.5) },
                    }}
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearAll}
                    disabled={!hasActiveFilters}
                    startIcon={<Clear />}
                    sx={{
                      borderColor: "#e5e7eb",
                      color: "#6b7280",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      px: 3,
                      py: 0.75,
                      borderRadius: "8px",
                      "&:hover": {
                        borderColor: "#ef4444",
                        color: "#ef4444",
                        backgroundColor: alpha("#ef4444", 0.04),
                      },
                    }}
                  >
                    Limpiar todo
                  </Button>
                  {reportData && (
                    <Button
                      variant="contained"
                      onClick={() => setPdfDialogOpen(true)}
                      startIcon={<PictureAsPdf />}
                      sx={{
                        backgroundColor: "#dc004e",
                        color: "#ffffffff",
                        textTransform: "none",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        px: 3,
                        py: 0.75,
                        borderRadius: "8px",
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#b0003a" },
                      }}
                    >
                      Exportar PDF
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </MDBox>

      {submitError && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            mb: 3,
            border: "1px solid #fee2e2",
            backgroundColor: "#fef2f2",
            p: 2,
          }}
        >
          <MDTypography variant="body2" color="error">
            {submitError}
          </MDTypography>
        </Paper>
      )}

      {reportData && (
        <>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  CIERRE DE CAJA
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: cierreCajaColumns, rows: cierreCajaRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  MOVIMIENTO DIARIO POR TARIFA
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: movimientoColumns, rows: movimientoRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  RECAUDOS
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: recaudosColumns, rows: recaudosRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  CONTADORES DE CAJERO
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: contadoresColumns, rows: contadoresRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  CONTEO VEHICULAR
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: conteoResumenColumns, rows: conteoResumenRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  CONTEO VEHICULAR (DETALLADO)
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: conteoVehicularColumns, rows: conteoVehicularRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  RECAUDO POR PERDIDOS
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: recaudoPerdidosColumns, rows: recaudoPerdidosRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  RECAUDO POR MISCELANEOS
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: recaudoPerdidosColumns, rows: recaudoMiscelaneosRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  RECAUDO POR RECARGAS DE PREPAGO
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: recaudoPerdidosColumns, rows: recaudoPrepagoRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  RECAUDO POR IMPUESTOS
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: recaudoPerdidosColumns, rows: recaudoImpuestosRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  DESCUENTO DE COMERCIOS
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: comerciosColumns, rows: comerciosRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  PAGOS CON PREPAGO
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: pagosPrepagoColumns, rows: pagosPrepagoRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
          <Paper
            elevation={0}
            sx={{ borderRadius: "8px", overflow: "hidden", mb: 3, border: "1px solid #e2e8f0" }}
          >
            <MDBox>
              <MDBox p={2} borderBottom="1px solid" borderColor="divider">
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1a202c" }}>
                  FORMAS DE PAGO
                </MDTypography>
              </MDBox>
              <DataTable
                table={{ columns: formasPagoColumns, rows: formasPagoRows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [], showEntriesPerPage: false }}
                showTotalEntries={false}
                noEndBorder={true}
                canSearch={false}
                pagination={{ variant: "gradient", color: "success" }}
              />
            </MDBox>
          </Paper>
        </>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }
      `}</style>
    </MDBox>
  );
};

FormInforme.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    init: PropTypes.string,
    end: PropTypes.string,
    user_kme: PropTypes.string,
    box: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  loading: PropTypes.bool,
};

FormInforme.defaultProps = {
  initialData: {},
  loading: false,
};

export default FormInforme;
